import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircleOutlined, IdcardOutlined, EnvironmentOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ROUTES, START_DATE_BUFFER_DAYS } from '@utils/constants';
import { formatDurationRange, calculateEstimatedPickupTime } from '@utils/formatters';
import { getMinStartDate } from '@utils/dateFormatter';
import { RoutingDisclaimer } from '@components/ui/RoutingDisclaimer/RoutingDisclaimer';
import { createStudent, updateStudent } from '@api/students';
import { getDistanceMatrix } from '@api/maps';
import { getShiftById } from '@api/shifts';
import { useAuth } from '../../context/AuthContext';
import { useToastContext } from '../../context/ToastContext';
import { getStudentSession, clearStudentSession } from '@utils/sessionStorage';
import { AppHeader } from '@components/layout/AppHeader';
import { SkeletonDetailGrid } from '../../components/ui/SkeletonLoader';
import { LocationCard } from '@components/ui/LocationCard';
import { formatDate } from '../../utils/dateFormatter';
import { filterShiftsByGrade } from '@utils/shiftFilter';
import type { ShiftDetails, Campus } from '@api/types/smithy';
import './styles/reviewdetails.css';
import '@styles/loader.css';

interface ReviewDetailsState {
  formData: any;
  campuses: any[];
  shifts: any[];
  pickupLocation: any;
  startDate?: string;
  transportType?: string;
  tripType?: string;
  pricingConfig?: any;
  studentId?: string;
  student?: any;
  distance?: number;
  distanceText?: string;
  duration?: number;
  durationText?: string;
  estimatedPickupTime?: string;
}

interface ValidationErrors {
  distance?: string;
  date?: string;
  shift?: string;
}

// Validation helper function to check if shift is valid
const isShiftValid = (
  shift: ShiftDetails | null,
  grade: string | undefined,
  campus: Campus | null | undefined
): boolean => {
  if (!shift || !grade || !campus) return false;

  // Check if shift is in ACTIVE state (if status property exists)
  if (shift?.status !== 'ACTIVE') {
    return false;
  }

  if (shift?.providerId && campus?.id && shift?.providerId !== campus.id) {
    return false;
  }

  // Check if shift is active (within valid date range)
  const now = dayjs();
  if (shift?.validUntil && dayjs(shift?.validUntil).isBefore(now)) {
    return false; // Shift has ended
  }

  // Check if shift matches the grade/class
  const filteredShifts = filterShiftsByGrade([shift], grade);
  return filteredShifts.length > 0;
};

export const ReviewDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ReviewDetailsState;
  const { parentId } = useAuth();
  const { addToast } = useToastContext();
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [calculatedDistance, setCalculatedDistance] = useState<number | null>(null);
  const [calculatedDuration, setCalculatedDuration] = useState<number | null>(null);
  const [distanceText, setDistanceText] = useState<string | null>(null);
  const [durationText, setDurationText] = useState<string | null>(null);
  const [distanceError, setDistanceError] = useState<string | null>(null);
  const [dropOffAddress, setDropOffAddress] = useState<string | null>(null);
  const [estimatedPickupTime, setEstimatedPickupTime] = useState<string | null>(null);
  const [fetchedShift, setFetchedShift] = useState<ShiftDetails | null>(null);

  // Refs for scroll-to-error validation
  const distanceRef = React.useRef<HTMLDivElement>(null);
  const dateRef = React.useRef<HTMLDivElement>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  
  // Get data from state (passed from PickupLocation)
  const session = getStudentSession();

  const getBufferedDateStr = () => {
    return getMinStartDate(START_DATE_BUFFER_DAYS);
  };
  const formData = session?.formData || state?.formData;
  const pickupLocation = state?.pickupLocation || session?.pickupLocation;
  const transportType = state?.transportType || session?.transportType;
  const tripType = state?.tripType || session?.tripType;
  const startDate = state?.startDate || session?.startDate;
  const pricingConfig = state?.pricingConfig || session?.pricingConfig;
  
  // Get distance from state first, then fallback to session
  const distanceFromState = state?.distance;
  const distanceTextFromState = state?.distanceText;
  const durationFromState = state?.duration;
  const durationTextFromState = state?.durationText;
  const estimatedPickupTimeFromState = state?.estimatedPickupTime;
  
  // Get shift date range from formData
  const getShiftDateRange = () => {
    if (formData?.shift?.validFrom && formData?.shift?.validUntil) {
      return {
        validFrom: formData.shift.validFrom,
        validUntil: formData.shift.validUntil,
      };
    }
    return null;
  };

  // Check if start date is within valid range
  const isStartDateValid = () => {
    if (!startDate) return false;
    
    const shiftRange = getShiftDateRange();
    const selectedDate = dayjs(startDate);
    
    // Date must be at least START_DATE_BUFFER_DAYS from today (IST)
    const minAllowedDate = dayjs(getMinStartDate(START_DATE_BUFFER_DAYS));
    const isAfterBuffer = selectedDate.isSame(minAllowedDate) || selectedDate.isAfter(minAllowedDate);
    if (!isAfterBuffer) return false;
    
    // If no shift range, date just needs to meet buffer requirement
    if (!shiftRange) return true;
    
    const shiftValidFrom = dayjs(shiftRange.validFrom);
    const shiftValidUntil = dayjs(shiftRange.validUntil);
    
    const isWithinShiftRange = (selectedDate.isSame(shiftValidFrom) || selectedDate.isAfter(shiftValidFrom)) && 
           (selectedDate.isSame(shiftValidUntil) || selectedDate.isBefore(shiftValidUntil));
    
    return isWithinShiftRange;
  };

  // Format campus address from contact object
  const getCampusAddress = (): string | null => {
    const campus = formData?.campus;
    if (!campus) return null;
    
    // Check if address is available in contact
    if (campus?.contact?.address) {
      const addr = campus.contact.address;
      const parts = [];
      if (addr.primaryAddressLine) parts.push(addr.primaryAddressLine);
      if (addr.secondaryAddressLine) parts.push(addr.secondaryAddressLine);
      if (addr.city) parts.push(addr.city);
      if (addr.state) parts.push(addr.state);
      if (addr.postalCode) parts.push(addr.postalCode);
      if (addr.country) parts.push(addr.country);
      return parts.join(', ');
    }
    return null;
  };

  // Set drop-off address from campus contact (only from address, not from coordinates)
  useEffect(() => {
    const campus = formData?.campus;
    if (!campus) {
      setDropOffAddress(null);
      return;
    }
    
    // Only get address from contact.address
    const campusAddress = getCampusAddress();
    setDropOffAddress(campusAddress);
  }, [formData?.campus]);
  
  if (!formData) {
    navigate(ROUTES.STUDENT_DETAILS);
    return null;
  }

  useEffect(() => {
    setLoadingDetails(false);
  }, []);

  // Fetch shift by ID to get the latest shift details for validation
  useEffect(() => {
    const fetchShiftDetails = async () => {
      try {
        // Get shift ID from formData - could be formData.shift.id or formData.shiftId
        const shiftId = formData?.shift?.id || formData?.shiftId;
        
        if (!shiftId) {
          setFetchedShift(null);
          setValidationErrors(prev => {
            const updated = { ...prev };
            delete updated.shift;
            return updated;
          });
          return;
        }

        // Fetch the latest shift details by ID
        const latestShift = await getShiftById(shiftId);
        setFetchedShift(latestShift);
        setValidationErrors(prev => {
          const updated = { ...prev };
          delete updated.shift;
          return updated;
        });
      } catch (error) {
        console.error('Error fetching shift details:', error);
        setFetchedShift(null);
        setValidationErrors(prev => ({ 
          ...prev, 
          shift: 'Failed to fetch shift details. Please try again or contact support.' 
        }));
      }
    };

    fetchShiftDetails();
  }, [formData?.shift?.id, formData?.shiftId]);

  // Calculate distance between student location and campus location using new API
  useEffect(() => {
    const calculateDistance = async () => {
      try {
        // Check if we have all required data
        if (!pickupLocation?.coordinates?.lat || !pickupLocation?.coordinates?.lng) {
          console.warn('Pickup location coordinates not available');
          return;
        }

        if (!formData?.campus?.location?.lat || !formData?.campus?.location?.lng) {
          console.warn('Campus location not available');
          return;
        }

        // If distance is already provided in state, use it
        if (distanceFromState) {
          setCalculatedDistance(distanceFromState);
          setCalculatedDuration(durationFromState || null);
          setDistanceText(distanceTextFromState || null);
          setDurationText(durationTextFromState || null);
          return;
        }

        // Fetch distance using the new API
        const result = await getDistanceMatrix(
          pickupLocation.coordinates.lat,
          pickupLocation.coordinates.lng,
          formData.campus.location.lat,
          formData.campus.location.lng
        );

        // Check if distance is valid (greater than 0)
        if (!result.distanceMeters || result.distanceMeters <= 0) {
          setDistanceError('Unable to calculate distance. Please try selecting a different location.');
          setCalculatedDistance(null);
          setCalculatedDuration(null);
          setDistanceText(null);
          setDurationText(null);
          return;
        }

        setCalculatedDistance(result.distanceKm);
        setCalculatedDuration(result.durationMinutes);
        setDistanceText(result.distanceText);
        setDurationText(result.durationText);
        setDistanceError(null);
      } catch (error) {
        console.error('Error calculating distance:', error);
        setDistanceError('Unable to calculate distance. Please try again.');
        // SelectPlans will handle fallback if needed
      }
    };

    calculateDistance();
  }, [pickupLocation, formData?.campus, distanceFromState]);

  // Calculate estimated pickup time when duration and shift start time are available
  useEffect(() => {
    // First, check if we have estimated pickup time from state (passed from PickupLocation)
    if (estimatedPickupTimeFromState) {
      setEstimatedPickupTime(estimatedPickupTimeFromState);
      return;
    }

    // Otherwise, calculate it locally if we have the required data
    if (calculatedDuration && formData?.shift?.schedule?.startsAt) {
      const pickupTime = calculateEstimatedPickupTime(
        formData.shift?.schedule?.startsAt,
        calculatedDuration
      );
      setEstimatedPickupTime(pickupTime);
    } else {
      setEstimatedPickupTime(null);
    }
  }, [calculatedDuration, formData?.shift?.schedule?.startsAt, estimatedPickupTimeFromState]);

  // Helper function to get campus name
  const getCampusName = () => {
    // Check if campus object is passed directly
    if (formData.campus?.name) {
      return formData.campus.name;
    }
    // Fallback to campusId if it's an object with name
    return null;
  };

  // Helper function to get shift name
  const getShiftName = () => {
    // Check if shift object is passed directly
    if (formData.shift?.displayLabel) {
      return formData.shift.displayLabel;
    }
    if (formData.shift?.name) {
      return formData.shift.name;
    }
    // Fallback to shiftId if it's an object with displayLabel or name
    if (formData.shiftId?.displayLabel) {
      return formData.shiftId.displayLabel;
    }
    if (formData.shiftId?.name) {
      return formData.shiftId.name;
    }
    return 'Not selected';
  };

  // Format grade the same way as GRADE_OPTIONS in constants.ts
  const formatGrade = (grade?: string) => {
    if (!grade) return '';
    return grade
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get formatted grade label
  const getGradeLabel = (): string => {
    // Fallback: format grade directly if it's a string
    if (formData.grade && typeof formData.grade === 'string') {
      return formatGrade(formData.grade);
    }
    // First try to get from label (already formatted in dropdown)
    if (formData.grade?.label) {
      return formatGrade(formData.grade.label);
    }
    // Fallback: format the value the same way as GRADE_OPTIONS
    if (formData.grade?.value) {
      return formatGrade(formData.grade.value);
    }
    return '';
  };

  const handleEditDetails = () => {
    navigate(ROUTES.STUDENT_DETAILS, {
      state: {
        formData,
        campuses: state?.campuses || [],
        shifts: state?.shifts || [],
      },
    });
  };

  const handleContinueToPlans = async () => {
    // Validate before proceeding
    const newErrors: ValidationErrors = {};

    if (!calculatedDistance || calculatedDistance < 0) {
      newErrors.distance = 'Distance could not be calculated. Please go back and verify your pickup location.';
    }
    if (!isStartDateValid()) {
      const minDate = dayjs(getMinStartDate(START_DATE_BUFFER_DAYS)).format('DD/MM/YYYY');
      const shiftRange = getShiftDateRange();
      if (shiftRange) {
        const shiftEnd = dayjs(shiftRange.validUntil).format('DD/MM/YYYY');
        newErrors.date = `Please select a start date between ${minDate} - ${shiftEnd}`;
      } else {
        newErrors.date = `Please select a start date from ${minDate} or later`;
      }
    }

    // Validate shift using the fetched shift details for the latest validation
    const gradeValue = typeof formData.grade === 'string' ? formData.grade : formData.grade?.value;
    // Use fetched shift for validation - do NOT fall back to stale formData.shift
    const selectedShift = fetchedShift;
    const selectedCampus = formData.campus;
    
    if (!isShiftValid(selectedShift, gradeValue, selectedCampus)) {
      newErrors.shift = 'The selected shift is no longer available or does not match your campus and grade. Please edit and select a different shift.';
    }

    setValidationErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);
    
    try {
      // Build tags object with transport type, trip type, and distance
      const tags: { [key: string]: string | number } = {};
      if (transportType === 'ac') {
        tags['transportType'] = 'AC';
      } else if (transportType === 'non-ac') {
        tags['transportType'] = 'NON_AC';
      }
      
      // Add trip type - use tripType from state/session if available, otherwise use pricingConfig
      if (tripType) {
        tags['tripType'] = tripType;
      } else if (pricingConfig?.packageType) {
        tags['tripType'] = pricingConfig.packageType;
      }
      
      // Add distance tags
      if (calculatedDistance) {
        tags['distanceToCampus'] = calculatedDistance || 0;
      }
      if (calculatedDuration) {
        tags['travelTime'] = Math.round(calculatedDuration);
      }

      // Check if student is already created (coming from Dashboard or Edit Details)
      // Use isNewStudent flag from session to determine if we should create or update
      const isNewStudent = session?.isNewStudent ?? true;
      const studentId = session?.studentId || state?.studentId;
      
      if (!isNewStudent && studentId) {
        // Student already exists, update instead of create
        // Get status from student object or session, fallback to PENDING_TOKEN_AMOUNT
        const studentStatus = state?.student?.status || 'PENDING_TOKEN_AMOUNT';
        
        const updatePayload = {
          primaryParentId: parentId || '',
          givenName: formData.givenName?.trim().split(" ").map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ") || '',
          familyName: formData.familyName?.trim().split(" ").map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ") || '',
          gender: formData.gender || '',
          grade: formData.grade?.value || formData.grade || '',
          campusId: formData.campus?.id || formData.campusId || '',
          shifts: formData.shift?.id ? [formData.shift.id] : (formData.shiftId ? [formData.shiftId] : []),
          startDate: startDate || getBufferedDateStr(), // Preserve existing startDate or use buffered date
          status: studentStatus,
          geoAddress: pickupLocation?.address || '',
          location: {
            lat: pickupLocation?.coordinates?.lat || 0,
            lng: pickupLocation?.coordinates?.lng || 0,
          },
          tags,
        };

        await updateStudent(studentId, updatePayload);

        // Store to localStorage
        localStorage.setItem('pendingSubscription', JSON.stringify({
          studentId,
          startDate: updatePayload.startDate,
        }));

        // Navigate to SelectPlans with updated student data
        navigate(ROUTES.SELECT_PLANS, {
          state: {
            studentId,
            student: {
              id: studentId,
              name: `${formData.givenName} ${formData.familyName}`,
              givenName: formData.givenName,
              familyName: formData.familyName,
              gender: formData.gender,
              grade: formData.grade?.value || formData.grade,
              campus: formData.campus,
              campusId: formData.campus?.id || formData.campusId,
              shifts: formData.shift?.id ? [formData.shift.id] : (formData.shiftId ? [formData.shiftId] : []),
              startDate: session?.startDate,
              status: 'PENDING_TOKEN_AMOUNT',
              geoAddress: pickupLocation?.address,
              location: {
                lat: pickupLocation?.coordinates?.lat || 0,
                lng: pickupLocation?.coordinates?.lng || 0,
              },
              tags,
            },
            distance: calculatedDistance,
            duration: calculatedDuration,
            distanceText: distanceText,
            durationText: durationText,
            estimatedPickupTime: estimatedPickupTime,
            campuses: state?.campuses || [],
            startDate: updatePayload.startDate,
          },
        });
        return;
      }

      // Prepare student creation payload for new students
      const studentPayload = {
        primaryParentId: parentId || '',
        givenName: formData.givenName?.trim().split(" ").map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ") || '',
        familyName: formData.familyName?.trim().split(" ").map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ") || '',
        gender: formData.gender || '',
        grade: formData.grade?.value || formData.grade || '',
        campusId: formData.campus?.id || formData.campusId || '',
        shifts: formData.shift?.id ? [formData.shift.id] : (formData.shiftId ? [formData.shiftId] : []),
        startDate: startDate || getBufferedDateStr(), // Use selected date or fallback to buffered date
        status: 'PENDING_TOKEN_AMOUNT',
        geoAddress: pickupLocation?.address || '',
        location: {
          lat: pickupLocation?.coordinates?.lat || 0,
          lng: pickupLocation?.coordinates?.lng || 0,
        },
        tags,
      };

      // Create student
      const studentResponse = await createStudent(studentPayload);

      const newStudentId = studentResponse.id;

      // Clear student session after successful creation
      clearStudentSession();

      // Store to localStorage
      localStorage.setItem('pendingSubscription', JSON.stringify({
        studentId: newStudentId,
        startDate: studentPayload.startDate,
      }));

      // Navigate to SelectPlans with student data and calculated distance
      navigate(ROUTES.SELECT_PLANS, {
        state: {
          studentId: newStudentId,
          student: {
            id: newStudentId,
            name: `${formData.givenName} ${formData.familyName}`,
            givenName: formData.givenName,
            familyName: formData.familyName,
            gender: formData.gender,
            grade: formData.grade?.value || formData.grade,
            campus: formData.campus,
            campusId: formData.campus?.id || formData.campusId,
            shifts: formData.shift?.id ? [formData.shift.id] : (formData.shiftId ? [formData.shiftId] : []),
            startDate: studentPayload.startDate,
            status: 'PENDING_TOKEN_AMOUNT',
            geoAddress: pickupLocation?.address,
            location: {
              lat: pickupLocation?.coordinates?.lat || 0,
              lng: pickupLocation?.coordinates?.lng || 0,
            },
            tags,
          },
          distance: calculatedDistance,
          duration: calculatedDuration,
          distanceText: distanceText,
          durationText: durationText,
          estimatedPickupTime: estimatedPickupTime,
          campuses: state?.campuses || [],
          startDate: studentPayload.startDate,
        },
      });
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Error',
        message: err.message || 'Failed to process student. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="review-details-page">
      {/* Header */}
      <AppHeader />

      {/* Main Content */}
      <div className="review-details-page__container">
        <div className="review-details-page__content">
          {/* Title */}
          <h1 className="review-details-page__title">Review your details</h1>

          {/* Info Banner with Distance */}
          <div className={`review-details-page__info-banner ${validationErrors.distance ? 'review-details-page__info-banner--error' : ''}`} ref={distanceRef}>
            <span className="review-details-page__info-icon">✓</span>
            <div className="review-details-page__info-content">
              <p className="review-details-page__info-text">
                You're saving 25–35 minutes per trip with optimized routes
              </p>
              {distanceError ? (
                <p className="review-details-page__info-subtext" style={{ color: '#ff4d4f' }}>
                  {distanceError}
                </p>
              ) : (
                <>
                  {distanceText && (
                    <p className="review-details-page__info-subtext">
                      Distance to campus: <strong>{distanceText}</strong>
                    </p>
                  )}
                  {estimatedPickupTime && (
                    <p className="review-details-page__info-subtext">
                        Estimated pickup time: <strong>{estimatedPickupTime}</strong>
                    </p>
                  )}
                  {calculatedDuration && (
                    <p className="review-details-page__info-subtext">Travel time: <strong>{formatDurationRange(calculatedDuration || 0)}</strong></p>
                  )}
                </>
              )}
            </div>
          </div>
          {validationErrors.distance && (
            <p className="review-details-page__error-banner">{validationErrors.distance}</p>
          )}

          {/* Student Details Section */}
          <div className="review-details-page__section">
            <div className="review-details-page__section-header">
              <div className="review-details-page__section-title">
                <span className="review-details-page__section-icon">👤</span>
                <span>Student Details</span>
              </div>
              <button
                className="review-details-page__edit-btn"
                onClick={handleEditDetails}
              >
                ✏️ Edit
              </button>
            </div>

            <div className="review-details-page__details-grid">
              {loadingDetails ? (
                <SkeletonDetailGrid count={5} />
              ) : (
                <>
                  {(formData.givenName || formData.familyName) && (
                    <div className="review-details-page__detail-item">
                      <span className="review-details-page__detail-label">Name</span>
                      <span className="review-details-page__detail-value">
                        {formData.givenName} {formData.familyName}
                      </span>
                    </div>
                  )}

                  {formData.gender && (
                    <div className="review-details-page__detail-item">
                      <span className="review-details-page__detail-label">Gender</span>
                      <span className="review-details-page__detail-value">
                        {formData.gender === 'M' && <span className="review-details-page__gender-icon" data-gender="M">♂ Male</span>}
                        {formData.gender === 'F' && <span className="review-details-page__gender-icon" data-gender="F">♀ Female</span>}
                        {formData.gender === 'NA' && <span className="review-details-page__gender-icon" data-gender="NA">◯ Other</span>}
                      </span>
                    </div>
                  )}

                  {(formData.grade?.label || formData.grade?.value || (formData.grade && typeof formData.grade === 'string')) && (
                    <div className="review-details-page__detail-item">
                      <span className="review-details-page__detail-label">Grade</span>
                      <span className="review-details-page__detail-value">
                        {getGradeLabel()}
                      </span>
                    </div>
                  )}

                  {getCampusName() && getCampusName() !== 'Not selected' && (
                    <div className="review-details-page__detail-item">
                      <span className="review-details-page__detail-label">Drop Location</span>
                      <span className="review-details-page__detail-value">
                        {getCampusName()}
                      </span>
                    </div>
                  )}

                  {getShiftName() && getShiftName() !== 'Not selected' && (
                    <div className={`review-details-page__detail-item ${validationErrors.shift ? 'review-details-page__detail-item--error' : ''}`}>
                      <span className="review-details-page__detail-label">Shift</span>
                      <span className="review-details-page__detail-value">
                        {getShiftName()}
                      </span>
                    </div>
                  )}

                  {transportType && (
                    <div className="review-details-page__detail-item">
                      <span className="review-details-page__detail-label">Transport Type</span>
                      <span className="review-details-page__detail-value" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        {transportType === 'ac' && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>❄️ AC Transport</span>}
                        {transportType === 'non-ac' && <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>🌬️ Non-AC Transport</span>}
                      </span>
                    </div>
                  )}

                  {(() => {
                    const displayTripType = tripType || pricingConfig?.packageType;
                    if (!displayTripType) return null;
                    
                    const tripTypeLabel = displayTripType === 'ONE_WAY' ? 'One Way' : 
                                         displayTripType === 'TWO_WAY' ? 'Two Way' : 
                                         displayTripType;
                    
                    return (
                      <div className="review-details-page__detail-item">
                        <span className="review-details-page__detail-label">Trip Type</span>
                        <span className="review-details-page__detail-value">
                          {tripTypeLabel}
                        </span>
                      </div>
                    );
                  })()}

                  {startDate && (
                    <div className={`review-details-page__detail-item ${validationErrors.date ? 'review-details-page__detail-item--error' : ''}`} ref={dateRef}>
                      <span className="review-details-page__detail-label">Subscription Start Date</span>
                      <span className="review-details-page__detail-value">
                        {formatDate(startDate)}
                      </span>
                    </div>
                  )}
                  
                  {getShiftDateRange()?.validUntil && (
                    <div className="review-details-page__detail-item">
                      <span className="review-details-page__detail-label">Subscription End Date</span>
                      <span className="review-details-page__detail-value">
                        {formatDate(getShiftDateRange()!.validUntil)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Location Section - Merged Pickup and Drop off */}
          {(pickupLocation?.address || dropOffAddress) && <div className="review-details-page__section">
            <LocationCard
              homeAddress={pickupLocation?.address || null}
              dropoffAddress={dropOffAddress || null}
              campusName={getCampusName()}
              isEdit={true}
              onEdit={() => navigate(ROUTES.PICKUP_LOCATION, { 
                state: {
                  formData,
                  campuses: state?.campuses || [],
                  shifts: state?.shifts || [],
                  pickupLocation,
                  startDate: state?.startDate,
                  transportType,
                  pricingConfig,
                }
              })}
            />
          </div>
}
          {/* Why Zero Section */}
          <div className="review-details-page__why-zero">
            <div className="review-details-page__why-zero-header">
              <span className="review-details-page__why-zero-icon">🛡️</span>
              <span className="review-details-page__why-zero-title">WHY ZERO?</span>
            </div>
            <p className="review-details-page__why-zero-text">
              Most low-cost options overload vehicles to cut costs. <strong>Zero Moblt</strong> follows compliant seating, verified drivers, and fixed routes.
            </p>
            <div className="review-details-page__features">
              <div className="review-details-page__feature">
                <CheckCircleOutlined className="review-details-page__feature-icon review-details-page__feature-icon--legal" />
                <span className="review-details-page__feature-text">Legal Seating</span>
              </div>
              <div className="review-details-page__feature">
                <IdcardOutlined className="review-details-page__feature-icon review-details-page__feature-icon--verified" />
                <span className="review-details-page__feature-text">Verified Drivers</span>
              </div>
              <div className="review-details-page__feature">
                <EnvironmentOutlined className="review-details-page__feature-icon review-details-page__feature-icon--fixed" />
                <span className="review-details-page__feature-text">Fixed Routes</span>
              </div>
            </div>
          </div>

          {/* Routing availability disclaimer */}
          <RoutingDisclaimer variant="full" />

          {/* Footer Buttons */}
          <div className="review-details-page__footer">
            {Object.keys(validationErrors).length > 0 && (
              <div className="review-details-page__error-banner">
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  {validationErrors.distance && (
                    <li>{validationErrors.distance}</li>
                  )}
                  {validationErrors.date && (
                    <li>{validationErrors.date}</li>
                  )}
                  {validationErrors.shift && (
                    <li>{validationErrors.shift}</li>
                  )}
                </ul>
              </div>
            )}
            <button
              className={`review-details-page__continue-btn ${loading ? 'btn-loading' : ''}`}
              onClick={handleContinueToPlans}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="btn-loader btn-loader--dark"></span>
                  Loading...
                </>
              ) : (
                'Continue to Plans'
              )}
            </button>
          </div>

          {/* Loading Modal Overlay */}
          {loading && (
            <div className="review-details-page__modal-overlay">
              <div className="review-details-page__modal">
                <span className="btn-loader btn-loader--dark" style={{ width: 40, height: 40 }}></span>
                <h3>Processing</h3>
                <p>Please wait while we save your details...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
